import { useEffect, useState } from "react";

/* Shared loading primitives.
 *
 * The repo had three separate ideas of "busy": the dashboard's skeletons, the
 * portal's `Spinner`, and a handful of bare "Loading…" strings. These are the
 * pieces the rest needs and none of them had — an inline spinner that sits in a
 * sentence, and the rule about when a spinner is worth showing at all.
 */

/** Hold a pending flag down until the work has lasted long enough to report.
 *
 *  A transition that resolves inside a frame still flips `isPending` true, and
 *  a spinner rendered straight off it appears and vanishes in ~16ms. That reads
 *  as a glitch rather than as progress, and a page told to show spinners
 *  "everywhere" is exactly the page that ends up flickering. The flag rises
 *  only after `delayMs` of continuous work, and falls the instant the work
 *  ends — so fast switches stay silent and slow ones are always announced.
 *
 *  Note it can only rise on the client: on the server no effect runs, so a
 *  server-rendered page never ships a spinner for work that has not started.
 */
export function useSettledPending(pending: boolean, delayMs = 120): boolean {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!pending) {
      setSettled(false);
      return;
    }
    const timer = setTimeout(() => setSettled(true), delayMs);
    return () => clearTimeout(timer);
  }, [pending, delayMs]);
  return settled;
}

/**
 * A spinner sized to sit inside a line of text.
 *
 * `label` is what a screen reader hears, and is the only thing it hears: the
 * ring itself is decorative. `inline` callers that already say what is loading
 * in visible text pass `labelled={false}` so the announcement is not doubled.
 */
export function InlineSpinner({
  label,
  labelled = true,
  className = "",
}: {
  label: string;
  labelled?: boolean;
  className?: string;
}) {
  return <span
    className={`inline-spinner ${className}`.trim()}
    role={labelled ? "status" : undefined}
    aria-label={labelled ? label : undefined}
    aria-hidden={labelled ? undefined : "true"}
  />;
}

/** Spinner plus its own caption, for a row or panel that is still filling in. */
export function PendingNote({ children, className = "" }: { children: string; className?: string }) {
  return <span className={`pending-note ${className}`.trim()} role="status">
    <InlineSpinner label={children} labelled={false} />
    {children}
  </span>;
}
